/*
 * Two-language string table for the landing page.
 *
 * Deliberately not vue-i18n: one page, a flat table, an image built offline from a
 * baked node_modules. A reactive locale plus a lookup is the whole thing.
 *
 * The copy is the Sept-2026 redesign's (second round), both languages as written there. The privacy
 * text is the platform's own (resources/data/translations_{es,en}.json -> privacy), so
 * silveribis.com and app.silveribis.com say the same thing.
 */
import { reactive } from 'vue';

const STORE_KEY = 'si_lang';

/* Verbatim from the platform's translations JSON (privacy.sections). */
const PRIVACY_ES = {
    "title": "Privacidad",
    "sections": [
        {
            "title": "POLÍTICA DE PRIVACIDAD",
            "content": "La confianza y privacidad de nuestros usuarios son fundamentales para Silveribis. Por ello, es importante que conozcas cómo protegemos la privacidad y la seguridad de tus datos personales, cumpliendo con todas las leyes aplicables, incluyendo el Reglamento General de Protección de Datos (RGPD) de Europa. Para que Silveribis funcione correctamente, es necesario recopilar ciertos datos personales, los cuales se utilizarán únicamente para llevar a cabo las actividades inherentes a nuestro servicio, sin que esto implique la venta o distribución de dichos datos. Silveribis está legalmente obligado a cumplir con todos los requisitos de seguridad y legales para proteger la información que nos proporcionas para los fines descritos anteriormente."
        },
        {
            "title": "Datos personales",
            "content": "Al registrarte, Silveribis solicitará la siguiente información necesaria para el correcto funcionamiento de la plataforma: <ul><li> - Nombre </li><li> - Apellido </li><li> - País de residencia </li><li> - Dirección </li><li> - Ciudad de residencia </li><li> - Código postal </li><li> - Número telefónico </li><li> - País de facturación </li><li> - Dirección de facturación </li><li> - Ciudad de facturación </li><li> - Código postal de facturación </li><li> - Correo electrónico </li><li> - Contraseña </li><li> - Nombre de compañía </li><li> - Dirección de IP </li></ul>"
        },
        {
            "title": "Cómo usaremos tus datos",
            "content": "Los datos personales que nos proporcionas se utilizarán exclusivamente para los siguientes propósitos: <ul><li>• Actividades relacionadas con nuestro servicio, conteo de visitas a artículos con derechos de autor, y facturación correspondiente.</li><li>• Notificaciones sobre nuevas características o cambios en las existentes.</li><li>• Análisis de los hábitos de consumo en la plataforma para proponer mejoras.</li></ul>"
        },
        {
            "title": "Compromiso de confidencialidad",
            "content": "Silveribis está completamente comprometido con la privacidad de sus usuarios y no divulgará información personal a terceros bajo ninguna circunstancia, a menos que haya una orden judicial. Para proteger tu privacidad, implementamos las medidas organizativas, técnicas y de seguridad más rigurosas para salvaguardar tu información y evitar alteraciones, pérdidas o accesos no autorizados."
        },
        {
            "title": "Cookies",
            "content": "Silveribis utiliza cookies, que son archivos de texto almacenados en tu navegador. Estos archivos permiten que nuestro sistema identifique tu sesión y optimice tu experiencia como usuario. Las cookies en Silveribis tienen una duración de tres meses y no contienen información que permita a terceros contactarte. Puedes ajustar la configuración de tu navegador para controlar la duración de las cookies o para bloquear su acceso por parte de Silveribis."
        },
        {
            "title": "Almacenamiento de información por parte de Silveribis",
            "content": "Silveribis almacena tus datos y dirección IP en una base de datos. Solo almacenamos la información mínima necesaria para operar nuestros servicios y cumplir con las normas de derechos de autor. Tus datos pueden eliminarse en cualquier momento, y se eliminarán dentro de los 5 días hábiles posteriores a tu solicitud. Los datos de usuarios registrados sin contrato activo se mantendrán para que puedan seguir utilizando nuestros servicios de manera limitada, hasta que se nos notifique su eliminación. Almacenamos tus datos para: <br>- Facturación <br>- Cumplir con las leyes de propiedad intelectual en los países donde operamos <br>- Personalizar nuestros productos y servicios."
        },
        {
            "title": "Derechos a acceder, editar y remover tus datos personales",
            "content": "Todos nuestros usuarios tienen el derecho de acceder, corregir y eliminar sus datos personales en nuestra base de datos. Para hacerlo, debes enviar un correo electrónico a <a href='mailto:hello@silveribis.com'>hello@silveribis.com</a>. Las solicitudes de acceso se procesan en un plazo de 5 días hábiles, mientras que las modificaciones o eliminaciones tardan 7 días hábiles."
        },
        {
            "title": "",
            "content": "Email: <a href='mailto:hello@silveribis.com'>hello@silveribis.com</a> Sitio web: <a href='https://www.silveribis.com'>www.silveribis.com</a>"
        }
    ]
};

const PRIVACY_EN = {
    "title": "Privacy",
    "sections": [
        {
            "title": "PRIVACY POLICY",
            "content": "The trust and privacy of our users are fundamental to Silveribis. Therefore, it is important for you to know how we protect your personal data's privacy and security, complying with all applicable laws, including the General Data Protection Regulation (GDPR) of Europe. For Silveribis to function correctly, it is necessary to collect certain personal data, which will only be used to carry out activities inherent to our service, without implying the sale or distribution of such data. Silveribis is legally obligated to comply with all security and legal requirements to protect the information you provide us for the purposes described above."
        },
        {
            "title": "Personal Data",
            "content": "When you register, Silveribis will request the following information necessary for the proper functioning of the platform: <ul><li>- First Name</li><li>- Last Name</li><li>- Country of Residence</li><li>- Address</li><li>- City of Residence</li><li>- Postal Code</li><li>- Phone Number</li><li>- Billing Country</li><li>- Billing Address</li><li>- Billing City</li><li>- Billing Postal Code</li><li>- Email</li><li>- Password</li><li>- Company Name</li><li>- IP Address</li></ul>"
        },
        {
            "title": "How We Will Use Your Data",
            "content": "The personal data you provide will be used exclusively for the following purposes: <ul><li>• Activities related to our service, counting visits to copyrighted articles, and corresponding billing.</li><li>• Notifications about new features or changes to existing ones.</li><li>• Analysis of consumption habits on the platform to propose improvements.</li></ul>"
        },
        {
            "title": "Confidentiality Commitment",
            "content": "Silveribis is fully committed to the privacy of its users and will not disclose personal information to third parties under any circumstances, unless there is a court order. To protect your privacy, we implement the most stringent organizational, technical, and security measures to safeguard your information and prevent alterations, losses, or unauthorized access."
        },
        {
            "title": "Cookies",
            "content": "Silveribis uses cookies, which are text files stored in your browser. These files allow our system to identify your session and optimize your experience as a user. Cookies in Silveribis have a duration of three months and do not contain information that allows third parties to contact you. You can adjust your browser settings to control the duration of cookies or to block their access by Silveribis."
        },
        {
            "title": "Information Storage by Silveribis",
            "content": "Silveribis stores your data and IP address in a database. We only store the minimum necessary information to operate our services and comply with copyright laws. Your data can be deleted at any time, and it will be removed within 5 business days following your request. The data of registered users without an active contract will be retained so they can continue to use our services in a limited manner until we are notified of their deletion. We store your data for: <br>- Billing <br>- Compliance with intellectual property laws in the countries where we operate <br>- Personalizing our products and services."
        },
        {
            "title": "Rights to Access, Edit, and Remove Your Personal Data",
            "content": "All our users have the right to access, correct, and delete their personal data in our database. To do so, you must send an email to <a href='mailto:hello@silveribis.com'>hello@silveribis.com</a>. Access requests are processed within 5 business days, while modifications or deletions take 7 business days."
        },
        {
            "title": "",
            "content": "Email: <a href='mailto:hello@silveribis.com'>hello@silveribis.com</a> Website: <a href='https://www.silveribis.com'>www.silveribis.com</a>"
        }
    ]
};


export const messages = {
    es: {
        nav: { reports: 'Informes', platform: 'Plataforma', contact: 'Contacto', login: 'Entrar', platform_cta: 'Ir a la plataforma', cta: 'Empieza gratis' },
        hero: {
            badge: 'El primer informe es gratis',
            // the headline rotates through these three (HeroBand.vue)
            h1: 'Una palabra, un reporte mediático de tu marca',
            h2: 'Conoce qué dice la IA sobre tu marca',
            h3: 'Entérate de las historias que se publican sobre tu marca y tus competidores',
            sub: 'Escribe el nombre de una marca en lenguaje natural. Silveribis busca en toda la prensa digital del mundo y devuelve un informe PDF terminado: cobertura, temas, fuentes, valor y tendencia de sentimiento. Listo para presentar a tu cliente o a tu dirección.',
            cta: 'Empieza gratis', sample: 'Ver informe de muestra', tour: 'Ver cómo funciona',
        },
        mock: { title: 'INFORME · MI MARCA', meta: 'T3 2026 · 12 pág.', mentions: 'MENCIONES', mentions_n: '1.248', value: 'VALOR', value_n: '86,4 k€', trend: 'TENDENCIA DE SENTIMIENTO' },
        ask: {
            label: 'Pídelo en lenguaje natural:',
            quote: '“Coca-Cola en los últimos 30 días”',
            out: '→ informe.pdf',
            c1t: 'Toda la prensa digital', c1: 'Todos los medios digitales del mundo. Con televisión y radio en Colombia, México y España.',
            c2t: 'Análisis con IA', c2: 'Temas, fuentes, valor mediático y sentimiento, calculados y redactados automáticamente.',
            c3t: 'Un PDF terminado', c3: 'Portada, gráficos y conclusiones. Nada que maquetar: se presenta tal cual.',
        },
        sample: {
            title: 'Qué contiene el informe de muestra', lead: 'Doce o más páginas generadas a partir de una sola frase, con la portada de tu marca.',
            r1t: 'Cobertura y alcance', r1p: 'pág. 2', r1n: 'Menciones por medio y por día',
            r2t: 'Temas y mensajes', r2p: 'pág. 5', r2n: 'Qué se dice y quién lo dice',
            r3t: 'Fuentes y valor mediático', r3p: 'pág. 8', r3n: 'Valor mediático equivalente por fuente',
            r4t: 'Tendencia de sentimiento', r4p: 'pág. 11', r4n: 'Evolución a lo largo del periodo',
            download: 'Descargar el informe de muestra (PDF)',
        },
        platform: {
            title: 'La plataforma, detrás del informe', lead: 'El informe es el producto. Detrás hay una plataforma completa para equipos de comunicación.',
            c1t: 'Buscar y comprar artículos', c1: 'Busca en el archivo de prensa y compra artículos sueltos, sin suscripción.',
            c2t: 'Alertas', c2: 'Alertas de cobertura en tu correo, cada mañana o al instante.',
            c3t: 'Back-office', c3: 'Docubase, Hawkeye y Herald: documentación, seguimiento y distribución.',
            link: 'Ir a la plataforma →',
        },
        brand: {
            title: 'Pruébalo con tu propia marca',
            text: 'Escribe el nombre de tu marca y recibe tu primer informe gratis. Sin tarjeta de crédito, sin demo, sin llamada.', cta: 'Empieza gratis',
        },
        contact: {
            title: 'Contacto', name: 'Nombre', email: 'Correo electrónico', subject: 'Asunto', message: 'Mensaje',
            send: 'Enviar mensaje', sending: 'Enviando…',
            sent: 'Mensaje enviado. Te responderemos en uno o dos días laborables.',
            recaptcha: 'Marca la casilla de reCAPTCHA antes de enviar.',
            fix: 'Revisa los campos marcados.', fail: 'No se pudo enviar el mensaje. Inténtalo de nuevo en un momento.',
        },
        footer: { copy: '© 2026 Silveribis', privacy: 'Política de privacidad' },
        notfound: { title: 'Página no encontrada', text: 'La dirección no existe o ha cambiado.', home: 'Volver al inicio' },
        privacy: PRIVACY_ES,
    },
    en: {
        nav: { reports: 'Reports', platform: 'Platform', contact: 'Contact', login: 'Log in', platform_cta: 'Go to the platform', cta: 'Start free' },
        hero: {
            badge: 'Your first report is free',
            h1: 'One word, a media report on your brand',
            h2: 'See what AI says about your brand',
            h3: 'Keep up with the stories published about your brand and your competitors',
            sub: 'Type a brand name in plain language. Silveribis searches all the digital press worldwide and returns a finished PDF report: coverage, topics, sources, value and sentiment trend. Ready to present to your client or your leadership team.',
            cta: 'Start free', sample: 'See a sample report', tour: 'See how it works',
        },
        mock: { title: 'REPORT · MY BRAND', meta: 'Q3 2026 · 12 pp.', mentions: 'MENTIONS', mentions_n: '1,248', value: 'VALUE', value_n: '€86.4k', trend: 'SENTIMENT TREND' },
        ask: {
            label: 'Ask in plain language:',
            quote: '“Coca-Cola over the last 30 days”',
            out: '→ report.pdf',
            c1t: 'All the digital press', c1: 'All the digital media worldwide. With TV and radio in Colombia, Mexico and Spain.',
            c2t: 'AI analysis', c2: 'Topics, sources, media value and sentiment, computed and written up automatically.',
            c3t: 'A finished PDF', c3: 'Cover, charts and conclusions. Nothing to lay out: present it as it arrives.',
        },
        sample: {
            title: 'What the sample report contains', lead: 'Twelve+ pages generated from a single sentence, with your brand on the cover.',
            r1t: 'Coverage and reach', r1p: 'p. 2', r1n: 'Mentions by outlet and by day',
            r2t: 'Topics and messages', r2p: 'p. 5', r2n: 'What is said and who says it',
            r3t: 'Sources and media value', r3p: 'p. 8', r3n: 'Equivalent media value per source',
            r4t: 'Sentiment trend', r4p: 'p. 11', r4n: 'How it moved over the period',
            download: 'Download the sample report (PDF)',
        },
        platform: {
            title: 'The platform behind the report', lead: 'The report is the product. Behind it sits a full platform for communications teams.',
            c1t: 'Search and buy articles', c1: 'Search the press archive and buy individual articles, no subscription.',
            c2t: 'Alerts', c2: 'Coverage alerts in your inbox, every morning or as they happen.',
            c3t: 'Back-office', c3: 'Docubase, Hawkeye and Herald: documentation, tracking and distribution.',
            link: 'Go to the platform →',
        },
        brand: {
            title: 'Try it with your own brand',
            text: "Type your brand's name and get your first report free. No credit card, no demo, no call.", cta: 'Start free',
        },
        contact: {
            title: 'Contact', name: 'Name', email: 'Email', subject: 'Subject', message: 'Message',
            send: 'Send message', sending: 'Sending…',
            sent: "Message sent. We'll get back to you within one or two working days.",
            recaptcha: 'Tick the reCAPTCHA box before sending.',
            fix: 'Check the highlighted fields.', fail: "The message couldn't be sent. Please try again in a moment.",
        },
        footer: { copy: '© 2026 Silveribis', privacy: 'Privacy policy' },
        notfound: { title: 'Page not found', text: "That address doesn't exist or has moved.", home: 'Back to the start' },
        privacy: PRIVACY_EN,
    },
};

function initialLocale() {
    try {
        const saved = window.localStorage.getItem(STORE_KEY);

        if (saved && messages[saved]) {
            return saved;
        }
    } catch (e) {
        /* private browsing and blocked site data both throw here */
    }

    // Spanish is the home market, so it wins any es-* browser and is the fallback.
    const nav = (window.navigator.language || 'es').toLowerCase();

    return nav.indexOf('en') === 0 ? 'en' : 'es';
}

export const state = reactive({ locale: initialLocale() });

export function setLocale(l) {
    if (!messages[l]) {
        return;
    }

    state.locale = l;

    try {
        window.localStorage.setItem(STORE_KEY, l);
    } catch (e) {
        /* a rejected write must not stop the page from switching language */
    }

    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('lang', l);
    }
}

/* Dotted lookup. Returns the key itself when a string is missing so a gap shows up as
   an obvious "reports.title" on the page rather than silently rendering empty. */
export function t(path) {
    const parts = String(path).split('.');
    let cur = messages[state.locale];

    for (let i = 0; i < parts.length; i++) {
        if (cur == null || typeof cur !== 'object' || !(parts[i] in cur)) {
            return path;
        }

        cur = cur[parts[i]];
    }

    return typeof cur === 'string' ? cur : path;
}

export default {
    install(app) {
        app.config.globalProperties.$t = t;
        app.config.globalProperties.$lang = state;
        app.config.globalProperties.$setLocale = setLocale;

        if (typeof document !== 'undefined') {
            document.documentElement.setAttribute('lang', state.locale);
        }
    },
};
